/** Master data as the views need it (from the replica's entities with own pending changes). */
import { FULL_ACCESS, PROFILES, SETTINGS_ID, articleUnits, baseProduct, editsMasterData, formatEuro, importOf, inboundOf, inventoryOf, isPantry, isProfiled, pantryPricesOn, profileOf, recipeOf, reservationOf, searchScore, shownName, vintagesTracked } from "@lagerlog/core";
import type { Access, Booking, Entity, ImportRecord, InboundRecord, PlanReservation, Inventory, PackUnit, Recipe, Replica } from "@lagerlog/core";
import { IS_PANTRY, ownsArticle, ownsLager } from "./brand.ts";

export interface Pack extends PackUnit {
  barcode?: string;
}

export interface Article {
  id: string;
  name: string;
  /** The base unit: ml for a drink (docs/17-getraenke.md), else g, Stk. */
  unit: string;
  /** What it is counted and booked in, smallest first: the container (Fl., 750 ml) and the packs, factors in base units. */
  units: PackUnit[];
  /** Base units of the smallest of them: 750 for a bottle, 1 for an article without container. */
  bottle: number;
  tracking: "lot" | "quantity";
  packs: Pack[];
  barcodes: string[];
  category?: string;
  /** All fields as stored, among them those of the wine profile. */
  fields: Record<string, unknown>;
  /** Lower-case text the search looks in: name, card number, region, grapes. */
  search: string;
}

export interface Lager {
  id: string;
  name: string;
  profile?: string;
}

export interface Location {
  id: string;
  lager: string;
  name: string;
}

export interface Catalog {
  articles: Map<string, Article>;
  lagers: Map<string, Lager>;
  locations: Map<string, Location>;
  /** Barcode → article and how many base units it stands for (a case: 6). */
  barcodes: Map<string, { article: string; factor: number }>;
  inventories: Map<string, Inventory>;
  /** Master data `card` by id: the last printed card (guests, cellar: printedAt, variant, lines), and settings of the workspace (measures, settings). */
  cards: Map<string, Record<string, unknown>>;
  /** Tables taken in, with what was left out and the decisions (packages/core/src/import-record.ts). */
  imports: Map<string, ImportRecord>;
  /** Prepared drinks for the card (Spritz, Gin Tonic; packages/core/src/menu.ts). */
  recipes: Map<string, Recipe>;
  /** Eingänge booked from a document (packages/core/src/inbound-match.ts). */
  inbounds: Map<string, InboundRecord>;
  /** Plans of SpeisePlan with their reservations (V3, packages/core/src/reservations.ts). */
  reservations: PlanReservation[];
}

const str = (v: unknown, fallback = "") => (typeof v === "string" ? v : fallback);

/**
 * The articles staff and viewers see (F20, decided 18.10.2026): those with stock in their storages,
 * and those booked there lately, so eggs that ran out can be booked in again. The device holds only
 * granted storages, so its ledger is the scope.
 */
function articlesInUse(replica: Replica): Set<string> {
  const used = new Set<string>();
  for (const acc of replica.state().accounts.values()) if (acc.qty !== 0) used.add(acc.article);
  for (const b of replica.journal() as Booking[]) {
    if (b.kind === "convert") [b.fromArticle, b.toArticle].forEach((a) => used.add(a));
    else if (b.kind !== "cancel") used.add(b.article);
  }
  return used;
}

export function catalogOf(replica: Replica, access: Access = FULL_ACCESS): Catalog {
  const inUse = editsMasterData(access.role) ? null : articlesInUse(replica);
  const articles = new Map<string, Article>();
  const lagers = new Map<string, Lager>();
  const locations = new Map<string, Location>();
  const barcodes = new Map<string, { article: string; factor: number }>();
  const inventories = new Map<string, Inventory>();
  const cards = new Map<string, Record<string, unknown>>();
  const imports = new Map<string, ImportRecord>();
  const recipes = new Map<string, Recipe>();
  const inbounds = new Map<string, InboundRecord>();
  const reservations: PlanReservation[] = [];
  const hiddenLagers = new Set<string>();
  for (const e of replica.entityView().values()) add(e);
  // A household shares KellerLog and VorratsBuch (docs/18-vorratsbuch.md, V0-5): each app shows its own storages and articles.
  for (const [id, l] of locations) if (hiddenLagers.has(l.lager)) locations.delete(id);
  return { articles, lagers, locations, barcodes, inventories, cards, imports, recipes, inbounds, reservations };

  function add(e: Entity) {
    const f = e.fields;
    if (f.archived === true) return;
    if (e.type === "article") {
      if (!ownsArticle(f)) return;
      if (inUse && !inUse.has(e.id)) return;
      const packs = Array.isArray(f.packs) ? (f.packs as Pack[]) : [];
      const codes = Array.isArray(f.barcodes) ? (f.barcodes as string[]) : [];
      const { base, units } = articleUnits(f);
      articles.set(e.id, {
        id: e.id,
        name: isPantry(f) ? shownName(str(f.name, e.id)) : str(f.name, e.id),
        unit: base,
        units,
        bottle: units[0]!.factor,
        tracking: f.tracking === "lot" ? "lot" : "quantity",
        packs,
        barcodes: codes,
        ...(typeof f.category === "string" ? { category: f.category } : {}),
        fields: f,
        search: searchText(f, str(f.name, e.id)),
      });
      // The article's own code is on the bottle.
      for (const code of codes) barcodes.set(code, { article: e.id, factor: units[0]!.factor });
      for (const p of packs) if (p.barcode) barcodes.set(p.barcode, { article: e.id, factor: p.factor });
    } else if (e.type === "lager") {
      if (!ownsLager(f.profile)) return void hiddenLagers.add(e.id);
      lagers.set(e.id, { id: e.id, name: str(f.name, e.id), ...(typeof f.profile === "string" ? { profile: f.profile } : {}) });
    } else if (e.type === "inventory") {
      const inv = inventoryOf(e);
      if (inv) inventories.set(e.id, inv);
    } else if (e.type === "card") {
      cards.set(e.id, f);
    } else if (e.type === "import") {
      const record = importOf(e);
      if (record) imports.set(e.id, record);
    } else if (e.type === "recipe") {
      const recipe = recipeOf(e);
      if (recipe) recipes.set(e.id, recipe);
    } else if (e.type === "reservation") {
      const plan = reservationOf(e);
      if (plan) reservations.push(plan);
    } else if (e.type === "inbound") {
      const record = inboundOf(e);
      if (record) inbounds.set(e.id, record);
    } else if (e.type === "location") {
      locations.set(e.id, { id: e.id, lager: str(f.lager), name: str(f.name, e.id) });
    }
  }
}

/** Card number first: the staff asks for "die 47". */
function searchText(f: Record<string, unknown>, name: string): string {
  // A pantry article is also found by its brand, its base product and that one's other names („Passata“, B3).
  const base = isPantry(f) ? (baseProduct(f.base)?.names ?? []) : [];
  const extra = [f.listNo ? `nr ${f.listNo} ${f.listNo}` : "", f.region, f.country, f.appellation, Array.isArray(f.grapes) ? f.grapes.join(" ") : "", isPantry(f) ? f.brand : "", ...base];
  return [name, ...extra].filter((v) => typeof v === "string" && v !== "").join(" ").toLowerCase();
}

/** Every word of the query somewhere in the article's text, typos forgiven (core/src/search.ts). */
export function matchesQuery(a: Article, query: string): boolean {
  return query.trim() !== "" && searchScore(a.search, query) !== null;
}

/** The articles that fit the query, best first. */
export function findArticles(articles: Iterable<Article>, query: string, limit = Infinity): Article[] {
  if (query.trim() === "") return [];
  const hits: { a: Article; s: number }[] = [];
  for (const a of articles) {
    const s = searchScore(a.search, query);
    if (s !== null) hits.push({ a, s });
  }
  return hits
    .sort((x, y) => x.s - y.s || x.a.name.localeCompare(y.a.name, "de"))
    .slice(0, limit)
    .map((h) => h.a);
}

/** The profile a drink belongs to, or null for an article of no drink profile (demo food, spirits of the demo year). */
/** Whether the workspace keeps vintages (F23, `card/settings`); without, a wine is one per size and has no „o. J.“. */
export const keepsVintages = (catalog: Catalog) => vintagesTracked(catalog.cards.get(SETTINGS_ID));

/** Prices and values shown: for owners and managers (`rolePrices`), in VorratsBuch only with „Preise erfassen“ (C4). */
export const pricesShown = (catalog: Catalog | null, rolePrices: boolean) => rolePrices && (!IS_PANTRY || (catalog !== null && pantryPricesOn(catalog.cards.get(SETTINGS_ID))));

/** Glass sizes as decimals, „0,25 l“ instead of „1/4 l“ (B5): a setting kept with the own measures (`card/measures`). */
export const decimalGlasses = (catalog: Catalog | null) => catalog?.cards.get("measures")?.decimal === true;

export function drinkProfile(a: Article): string | null {
  if (typeof a.fields.profile === "string") return a.fields.profile;
  return isProfiled(a.fields) ? profileOf(a.fields) : null;
}

/** The drink profiles with at least one article, in their order; Wein always (docs/17-getraenke.md, Oberfläche). */
export function usedProfiles(catalog: Catalog): string[] {
  const used = new Set(["wein"]);
  for (const a of catalog.articles.values()) {
    const p = drinkProfile(a);
    if (p) used.add(p);
  }
  return PROFILES.map((p) => p.id).filter((id) => used.has(id));
}

/** Bottles (containers) in a quantity of base units; 0 for an article without container (g, Stk.). */
export const bottlesIn = (a: Article | undefined, qty: number) => (a && a.unit === "ml" && a.bottle > 1 ? qty / a.bottle : 0);

/** A quantity in the article's smallest unit: bottles for a drink, else the base unit. */
export const inUnits = (a: Article | undefined, qty: number) => (a ? qty / a.bottle : qty);

/** A number of bottles as shown: whole, or with one decimal for an opened one („3,5“). */
export const bottleCount = (n: number) => (Number.isInteger(n) ? String(n) : (Math.round(n * 10) / 10).toLocaleString("de-DE"));

/** Short names in words: „Fl.“ is „Flasche“. */
const WORDS_OF: Readonly<Record<string, [string, string]>> = { "Fl.": ["Flasche", "Flaschen"], "Pck.": ["Packung", "Packungen"], "Stk.": ["Stück", "Stück"] };

/** The article's container in words, one and many: „Flasche“, „Flaschen“; „Fass“, „Fässer“. */
export function containerWords(a: Article): { one: string; many: string } {
  const u = a.units[0]!;
  const [one, many] = WORDS_OF[u.name] ?? [u.name, u.plural ?? u.name];
  return { one, many };
}

/** Whether a table to take away needs a column for the container: kegs or cans among bottles. */
export const otherContainers = (articles: Iterable<Article | undefined>) => [...articles].some((a) => a && a.unit === "ml" && a.bottle > 1 && a.units[0]!.name !== "Fl.");

/**
 * Containers of several drinks, added up per kind: „120 Fl.“ and „3 Fässer“, never „123 Fl.“; a
 * keg or a can is no bottle. Articles without a container (g, Stk.) are left out, as in `bottlesIn`.
 */
export class Containers {
  private by = new Map<string, { n: number; one: string; many: string }>();

  add(a: Article | undefined, qty: number): this {
    if (!a || a.unit !== "ml" || a.bottle <= 1) return this;
    const u = a.units[0]!;
    const c = this.by.get(u.name) ?? { n: 0, one: u.name, many: u.plural ?? u.name };
    c.n += qty / a.bottle;
    this.by.set(u.name, c);
    return this;
  }

  /** All containers together, only to sort by. */
  get total(): number {
    return [...this.by.values()].reduce((s, c) => s + c.n, 0);
  }

  /** Whether there are other containers than bottles. */
  get mixed(): boolean {
    return [...this.by.values()].some((c) => c.n !== 0 && c.one !== "Fl.");
  }

  /** One part per kind, bottles first, then the most: ["120 Fl.", "3 Fässer"]; nothing: ["0 Fl."]. */
  parts(sign = false): string[] {
    const all = [...this.by.values()].filter((c) => c.n !== 0).sort((x, y) => Number(y.one === "Fl.") - Number(x.one === "Fl.") || Math.abs(y.n) - Math.abs(x.n));
    if (all.length === 0) return ["0 Fl."];
    return all.map((c) => `${sign && c.n > 0 ? "+" : c.n < 0 ? "−" : ""}${bottleCount(Math.abs(c.n))} ${Math.abs(c.n) === 1 ? c.one : c.many}`);
  }

  /** „120 Fl. + 3 Fässer“ */
  text(sign = false): string {
    return this.parts(sign).join(" + ");
  }
}

export const euro = formatEuro;
